import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Info, Mail, Shield, UserCheck, UserX, Save, AlertCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useUpdateSurveyAttributes } from '@/hooks/useSurveyQueries';
import type { ConsentType } from '@/types/survey';
import { getDefaultTemplate } from '@/utils/templateLoader';
import { getAvailablePlaceholders, SurveyData } from '@/utils/placeholderReplacer';

export interface ConsentTemplate {
  subject: string;
  body: string;
}

// ConsentType is now imported from types
interface ConsentTemplateEditorProps {
  consentType: ConsentType;
  consentTemplate: ConsentTemplate;
  onConsentTypeChange: (type: ConsentType) => void;
  onConsentTemplateChange: (template: ConsentTemplate) => void;
  surveyId?: string; // Optional for standalone consent editing
  showSaveButton?: boolean; // Show save button for standalone mode
  readonly?: boolean; // Make the editor readonly
  readonlyMessage?: string; // Message to show when readonly
  bodyOnlyReadonly?: boolean; // Make only the email body readonly
  surveyData?: SurveyData; // Survey data for placeholder replacement
  /** When true, changing consent type asks for confirmation before erasing the current template */
  confirmTemplateOverwrite?: boolean;
}


export const ConsentTemplateEditor: React.FC<ConsentTemplateEditorProps> = ({
  consentType,
  consentTemplate,
  onConsentTypeChange,
  onConsentTemplateChange,
  surveyId,
  showSaveButton = false,
  readonly = false,
  readonlyMessage,
  bodyOnlyReadonly = false,
  surveyData,
  confirmTemplateOverwrite = false,
}) => {
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const [pendingConsentType, setPendingConsentType] = useState<ConsentType | null>(null);
  
  const updateSurveyAttributesMutation = useUpdateSurveyAttributes({
    onSuccess: () => {
      toast.success('Consent template updated successfully!');
    },
    onError: (error) => {
      toast.error('Failed to update consent template');
      console.error('Failed to update consent template:', error);
    }
  });

  const applyConsentTypeChange = (type: ConsentType) => {
    // Check if switching from "no consent" to a consent type
    const isSwitchingToConsent = !consentType && (type === 'CONSENT_TYPE_ACTIVE_CONSENT' || type === 'CONSENT_TYPE_PASSIVE_CONSENT');
    
    if (isSwitchingToConsent) {
      setShowConsentDialog(true);
    }
    
    onConsentTypeChange(type);
    
    // Always replace with the default for the selected type (discards prior edits)
    const defaultTemplate = getDefaultTemplate(type);
    onConsentTemplateChange(defaultTemplate ?? { subject: '', body: '' });
  };

  const handleConsentTypeChange = (type: ConsentType) => {
    if (type === consentType) return;

    const hasExistingTemplate = Boolean(consentTemplate.subject?.trim() || consentTemplate.body?.trim());

    if (confirmTemplateOverwrite && hasExistingTemplate) {
      setPendingConsentType(type);
      setShowOverwriteDialog(true);
      return;
    }

    applyConsentTypeChange(type);
  };

  const handleConfirmOverwrite = () => {
    if (pendingConsentType !== null) {
      applyConsentTypeChange(pendingConsentType);
    }
    setPendingConsentType(null);
    setShowOverwriteDialog(false);
  };

  const handleCancelOverwrite = () => {
    setPendingConsentType(null);
    setShowOverwriteDialog(false);
  };

  const handleTemplateChange = (field: keyof ConsentTemplate, value: string) => {
    onConsentTemplateChange({
      ...consentTemplate,
      [field]: value,
    });
  };

  const handleSaveConsentTemplate = () => {
    if (!surveyId) {
      toast.error('Survey ID is required to save consent template');
      return;
    }

    updateSurveyAttributesMutation.mutate({
      surveyId,
      attributes: {
        consentType: consentType || null,
        consentTemplate: consentType ? consentTemplate : null,
      }
    });
  };

  const getConsentTypeDescription = (type: ConsentType) => {
    switch (type) {
      case 'CONSENT_TYPE_ACTIVE_CONSENT':
        return 'Parents must explicitly consent for their child to participate. No response means no participation.';
      case 'CONSENT_TYPE_PASSIVE_CONSENT':
        return 'Parents are notified and can opt-out. No response means participation is allowed.';
      default:
        return 'No consent process required for this survey.';
    }
  };

  const getConsentTypeIcon = (type: ConsentType) => {
    switch (type) {
      case 'CONSENT_TYPE_ACTIVE_CONSENT':
        return <UserCheck className="h-4 w-4" />;
      case 'CONSENT_TYPE_PASSIVE_CONSENT':
        return <UserX className="h-4 w-4" />;
      default:
        return <Shield className="h-4 w-4" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Consent Management
        </CardTitle>
        <CardDescription>
          Configure how parent consent is handled for this survey
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {readonly && readonlyMessage && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {readonlyMessage}
            </AlertDescription>
          </Alert>
        )}
        {/* Consent Type Selection */}
        <div className="space-y-4">
          <Label className="text-base font-medium">Consent Type</Label>
          <RadioGroup
            value={consentType}
            onValueChange={readonly ? () => {} : handleConsentTypeChange}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
            disabled={readonly}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="" id="no-consent" />
              <Label htmlFor="no-consent" className="flex items-center gap-2 cursor-pointer">
                <Shield className="h-4 w-4" />
                <div>
                  <div className="font-medium">No Consent Required</div>
                  <div className="text-sm text-muted-foreground">Direct participation</div>
                </div>
              </Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="CONSENT_TYPE_ACTIVE_CONSENT" id="active-consent" />
              <Label htmlFor="active-consent" className="flex items-center gap-2 cursor-pointer">
                <UserCheck className="h-4 w-4" />
                <div>
                  <div className="font-medium">Active Consent</div>
                  <div className="text-sm text-muted-foreground">Explicit permission required</div>
                </div>
              </Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="CONSENT_TYPE_PASSIVE_CONSENT" id="passive-consent" />
              <Label htmlFor="passive-consent" className="flex items-center gap-2 cursor-pointer">
                <UserX className="h-4 w-4" />
                <div>
                  <div className="font-medium">Passive Consent</div>
                  <div className="text-sm text-muted-foreground">Opt-out allowed</div>
                </div>
              </Label>
            </div>
          </RadioGroup>

          {consentType && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                {getConsentTypeDescription(consentType)}
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Consent Template Editor */}
        {consentType && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Consent Template</Label>
              <Badge variant="outline" className="flex items-center gap-1">
                {getConsentTypeIcon(consentType)}
                {consentType === 'CONSENT_TYPE_ACTIVE_CONSENT' ? 'Active' : 'Passive'}
              </Badge>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="consent-subject">Email Subject</Label>
                <Input
                  id="consent-subject"
                  value={consentTemplate.subject}
                  onChange={readonly ? () => {} : (e) => handleTemplateChange('subject', e.target.value)}
                  placeholder="Enter email subject..."
                  disabled={readonly}
                />
              </div>
              
              <div>
                <Label htmlFor="consent-body">Email Body</Label>
                {bodyOnlyReadonly && !readonly && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Email body is read-only when survey is not in "created" status
                  </p>
                )}
                <RichTextEditor
                  key={consentType}
                  content={consentTemplate.body}
                  onChange={readonly || bodyOnlyReadonly ? () => {} : (content) => handleTemplateChange('body', content)}
                  placeholder="Enter email body content..."
                  className={`min-h-[400px] ${readonly || bodyOnlyReadonly ? 'opacity-50 pointer-events-none' : ''}`}
                  availablePlaceholders={surveyData ? getAvailablePlaceholders(surveyData) : []}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Use the toolbar for formatting. Available placeholders: {surveyData ? getAvailablePlaceholders(surveyData).join(', ') : '[SCHOOL], [DISTRICT], [TIMEFRAME], [ASSESSMENT]'}
                </p>
              </div>
            </div>

            {showSaveButton && !readonly && (
              <div className="flex justify-end mt-6">
                <Button 
                  onClick={handleSaveConsentTemplate}
                  disabled={updateSurveyAttributesMutation.isPending}
                  className="flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {updateSurveyAttributesMutation.isPending ? 'Saving...' : 'Save Consent Template'}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
      
      {/* Consent Warning Dialog */}
      <Dialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Important information
            </DialogTitle>
            <DialogDescription className="text-left">
              You've selected to use our consent system. 
              Please edit carefully the consent template that will be sent to parents and save.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setShowConsentDialog(false)}>
              I understand
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template overwrite confirmation (edit flow) */}
      <Dialog
        open={showOverwriteDialog}
        onOpenChange={(open) => {
          if (!open) handleCancelOverwrite();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Change consent type?
            </DialogTitle>
            <DialogDescription className="text-left">
              Existing Consent Template will be erased
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleCancelOverwrite}>
              Cancel
            </Button>
            <Button onClick={handleConfirmOverwrite}>
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}; 